import { z } from 'zod';
import { uuidSchema } from '../primitives.js';

/**
 * Finding a rider at the counter.
 *
 * The delivery form names its rider by picking them from the registry, never
 * by typing a name that is then matched in the background. The matching
 * below decides only what is OFFERED; a person always does the choosing. Two
 * riders whose names share a few letters are two rows in the list, never one
 * guess — the same refusal logistics/riders.ts makes for imports, for the same
 * reason: the rider named on a delivery is the rider who gets paid for it.
 *
 * One matcher, used by the server's search and by the form as it filters, so
 * the list a counter sees and the list the API returns cannot disagree.
 */

/**
 * A Nigerian phone number, reduced to the ten digits after the trunk prefix.
 *
 * `+234 803 123 4567`, `2348031234567`, `08031234567`, `0803-123-4567` and
 * `8031234567` are one number, and all of them give `8031234567`. Anything
 * that is not recognisably a Nigerian mobile or landline comes back as its
 * bare digits, so it still compares equal to itself.
 */
export function phoneKey(raw: string | null | undefined): string {
  let digits = String(raw ?? '').replace(/\D/g, '');
  if (digits.startsWith('234') && digits.length >= 12) digits = digits.slice(3);
  else if (digits.startsWith('0') && digits.length === 11) digits = digits.slice(1);
  return digits;
}

/**
 * The same number in the form the counter writes it: `08031234567`.
 *
 * Null when it is not a ten-digit Nigerian number after normalising, so a
 * caller can tell "written differently" from "not a number".
 */
export function normalizeNigerianPhone(raw: string | null | undefined): string | null {
  const key = phoneKey(raw);
  return /^[1-9]\d{9}$/.test(key) ? `0${key}` : null;
}

/**
 * The digits of a number somebody is still typing, prefix removed: `0803`,
 * `+234803` and `803` are all the start of `803…`.
 */
function partialPhoneKey(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.startsWith('234') && digits.length > 3) return digits.slice(3);
  if (digits.startsWith('0')) return digits.slice(1);
  return digits;
}

/** Same number, however each was written. Empty never matches anything. */
export function samePhone(a: string | null | undefined, b: string | null | undefined): boolean {
  const ka = phoneKey(a);
  return ka.length > 0 && ka === phoneKey(b);
}

const fold = (s: string | null | undefined): string => String(s ?? '').trim().toLowerCase();

export interface RiderCandidate {
  id: string;
  externalId: string | null;
  name: string;
  phone: string | null;
  aliases?: readonly string[];
}

/**
 * The riders a query could mean, best first.
 *
 * Case-insensitive and partial on the name, an approved alias and the Rider
 * ID; on the phone, matched as digits after normalising, so typing `0803`,
 * `+234803` or `803` all find the same rider. A phone query needs three
 * digits: fewer would match nearly everybody.
 *
 * Ranked so an exact Rider ID or full phone number comes first, then names
 * that start with the query, then anything containing it — ranking, never
 * choosing. The caller shows the list.
 */
export function matchRiders<T extends RiderCandidate>(riders: readonly T[], query: string): T[] {
  const q = fold(query);
  if (!q) return [];
  const qDigits = /^[+\d\s()\-.]+$/.test(query.trim()) ? partialPhoneKey(query) : '';

  const scored: Array<{ rider: T; score: number }> = [];
  for (const rider of riders) {
    const id = fold(rider.externalId);
    const names = [fold(rider.name), ...(rider.aliases ?? []).map(fold)];
    const phone = phoneKey(rider.phone);
    let score = 0;
    if (id && id === q) score = 100;
    else if (qDigits.length >= 3 && phone && phone === qDigits) score = 95;
    else if (names.some((n) => n === q)) score = 90;
    else if (id && id.startsWith(q)) score = 70;
    else if (names.some((n) => n.startsWith(q) || n.split(/\s+/).some((w) => w.startsWith(q)))) score = 60;
    else if (qDigits.length >= 3 && phone.includes(qDigits)) score = 50;
    else if (id && id.includes(q)) score = 40;
    else if (names.some((n) => n.includes(q))) score = 30;
    if (score > 0) scored.push({ rider, score });
  }
  return scored
    .sort((a, b) => b.score - a.score || a.rider.name.localeCompare(b.rider.name))
    .map((s) => s.rider);
}

/** One rider a counter may put on a delivery at a given location. */
export const selectableRiderSchema = z.object({
  id: uuidSchema,
  externalId: z.string().nullable(),
  name: z.string(),
  phone: z.string().nullable(),
  aliases: z.array(z.string()),
  /** Null: works anywhere in the business. */
  branchId: uuidSchema.nullable(),
  /**
   * Whether the registry already holds everything a delivery needs. A rider
   * missing a Rider ID or phone can still be chosen, but the gap has to be
   * filled — on their record, by somebody allowed to edit riders — first.
   */
  complete: z.boolean(),
  /**
   * The business is in compatible rider validation and this rider was on the
   * books without a phone before phones were compulsory: the phone may be
   * typed for the delivery instead of taken from the record.
   */
  typedPhoneAllowed: z.boolean(),
});
export type SelectableRider = z.infer<typeof selectableRiderSchema>;

export const riderLookupQuerySchema = z.object({
  branchId: uuidSchema,
  q: z.string().trim().max(100).optional(),
});

export const RIDER_VALIDATION_MODES = ['strict', 'compatible'] as const;
export type RiderValidationMode = (typeof RIDER_VALIDATION_MODES)[number];

export const riderValidationInputSchema = z.object({ mode: z.enum(RIDER_VALIDATION_MODES) });

export interface RiderValidationState {
  mode: RiderValidationMode;
  activeRiders: number;
  /** Active riders whose record still lacks a Rider ID or a phone. */
  incomplete: Array<{ id: string; name: string; externalId: string | null; missing: Array<'riderId' | 'phone'> }>;
}
